import { PROFILE_EXTRACTION_SYSTEM_PROMPT } from "./prompts";
import { llmRequest } from "./client";

export interface ExtractedProfile {
  name: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  nationality: string | null;
  linkedin: string | null;
  language_dutch: string | null;
  language_english: string | null;
  seniority: string | null;
  sector_experience: string[];
  available_from: string | null;
  employer: string | null;
  confirmed_skills: {
    skill: string;
    years: number;
    evidence: string;
  }[];
  inferred_skills: {
    skill: string;
    confidence: "high" | "medium" | "low";
    inference_basis: string;
  }[];
}

const extractionSchema = {
  type: "object",
  properties: {
    name:              { type: ["string", "null"] },
    email:             { type: ["string", "null"] },
    phone:             { type: ["string", "null"] },
    location:          { type: ["string", "null"] },
    nationality:       { type: ["string", "null"] },
    linkedin:          { type: ["string", "null"] },
    language_dutch:    { type: ["string", "null"] },
    language_english:  { type: ["string", "null"] },
    seniority:         { type: ["string", "null"] },
    sector_experience: { type: "array", items: { type: "string" } },
    available_from:    { type: ["string", "null"] },
    employer:          { type: ["string", "null"] },
    confirmed_skills: {
      type: "array",
      items: {
        type: "object",
        properties: {
          skill: { type: "string" },
          years: { type: "integer" },
          evidence: { type: "string" },
        },
        required: ["skill", "years", "evidence"],
      },
    },
    inferred_skills: {
      type: "array",
      items: {
        type: "object",
        properties: {
          skill: { type: "string" },
          confidence: { type: "string", enum: ["high", "medium", "low"] },
          inference_basis: { type: "string" },
        },
        required: ["skill", "confidence", "inference_basis"],
      },
    },
  },
  required: [
    "name", "email", "phone", "location", "nationality", "linkedin",
    "language_dutch", "language_english", "seniority", "sector_experience",
    "available_from", "employer", "confirmed_skills", "inferred_skills",
  ],
};

// Signature preserved from v1.7 for caller compatibility; apiKey + model
// arguments are ignored in v2.0 (server-side proxy decides).
export async function extractProfileFromCV(
  _apiKey: string,
  _model: string,
  cvText: string,
): Promise<ExtractedProfile> {
  const userPrompt = `Extract the consultant's personal and professional details from the following CV text:\n\n${cvText}`;
  return llmRequest<ExtractedProfile>({
    systemPrompt: PROFILE_EXTRACTION_SYSTEM_PROMPT,
    userContent: [{ type: "text", text: userPrompt }],
    schema: extractionSchema,
    maxTokens: 2048,
  });
}
